import { yupResolver } from '@hookform/resolvers/yup';
import React, { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import LinkedInIcon from '@/Components/TalentMeshIcons/LinkedInIcon';
import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from '@/Components/shadcn';
import UIStrings from '@/Utils/UIStrings';
import { useLinkedInProfileSearch, useShortlistLinkedInProfile } from '../../../api/useShortlistedProfiles';
import { useShortlistedListContext } from '../utils/ShortlistedListContext';
import MatchingProfilesList from './MatchingProfilesList';
import { ShortlistedProfile } from '../../../api/DTO/ShortlistedProfile';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import { isUrl, linkedInFormSchema } from './form';
import HttpError from '@/Utils/HTTP/Errors/HttpError';
import YupValidationErrors from '@/Utils/YupValidationErrors';
import useAISearchCounters from '../../../api/useAISearchCounters';
import { FieldError } from '@/Components/shadcn/field';

const AddFromLinkedInButton = () => {
    const [open, setOpen] = useState(false);
    const form = useForm({ resolver: yupResolver(linkedInFormSchema) });
    const { trigger: shortlist, isMutating: isShortlisting } = useShortlistLinkedInProfile();
    const {
        data: searchResult,
        trigger: search,
        isMutating: isSearching,
        error: searchError,
        reset: resetSearch,
    } = useLinkedInProfileSearch();
    const { mutate: mutateShortlistedList } = useShortlistedListContext();
    const { mutate: mutateAISearchCounters } = useAISearchCounters();

    const [selectedProfiles, setSelectedProfiles] = useState<ShortlistedProfile[]>([]);
    const searchErrorMessage = useMemo(() => {
        if (searchError instanceof ResourceNotFoundError) {
            return (
                <div className="text-sm">
                    <p>{UIStrings.ProfileNotFoundThisMightHelp}</p>
                    <ul className="mt-1 list-disc pl-5">
                        <li>{UIStrings.ProfileNotFoundDoubleCheckUrl}</li>
                        <li>{UIStrings.ProfileNotFoundVisitOnLinkedIn}</li>
                        <li>{UIStrings.ProfileNotFoundUseAISearchInstead}</li>
                    </ul>
                </div>
            );
        }

        return null;
    }, [searchError]);

    const resetDialogState = () => {
        form.reset();
        setSelectedProfiles([]);
        resetSearch();
    };

    const handleOpenChange = (next: boolean) => {
        if (!next && isShortlisting) return;
        if (!next) resetDialogState();
        setOpen(next);
    };

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        if (isShortlisting) return;
        setOpen(false);
        resetDialogState();
    };

    const handleSearch = form.handleSubmit(async ({ linkedInId }) => {
        setSelectedProfiles([]);

        let linkedInUrl: string;

        if (isUrl(linkedInId)) {
            try {
                linkedInUrl = new URL(linkedInId).toString();
            } catch {
                linkedInUrl = `https://${linkedInId}`;
            }
        } else {
            linkedInUrl = `https://www.linkedin.com/in/${linkedInId}`;
        }

        try {
            const profiles = await search({ linkedInUrl });

            setSelectedProfiles([profiles]);
        } catch (error) {
            if (error instanceof ResourceNotFoundError) {
                form.setError('linkedInId', { message: UIStrings.ProfileNotFound, type: 'validate' });
            }

            if (error instanceof HttpError) {
                if (error.detail?.includes('Validation failed:')) {
                    form.setError('linkedInId', {
                        message: YupValidationErrors.UrlMustBeFromLinkedIn,
                        type: 'validate',
                    });
                } else {
                    form.setError('linkedInId', { message: error.detail, type: 'validate' });
                }
            }
        }
    });

    const handleShortlist = async () => {
        await shortlist({ profileId: selectedProfiles[0].id });
        toast.success(UIStrings.NumberOfCandidatesAddedToShortlist(1));
        mutateShortlistedList();
        mutateAISearchCounters();
        handleClose();
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                disabled={isShortlisting}
                data-cy="shortlistedAddFromLinkedInButton"
                className="ml-2"
            >
                {isShortlisting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {UIStrings.AddFromLinkedIn}
            </Button>
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent
                    data-cy="shortlistedAddFromLinkedInDialog"
                    className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 max-w-2xl duration-300"
                    onPointerDownOutside={(e) => {
                        if (isShortlisting) e.preventDefault();
                    }}
                    onEscapeKeyDown={(e) => {
                        if (isShortlisting) e.preventDefault();
                    }}
                    aria-describedby={undefined}
                >
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold">
                            {UIStrings.AddShortlistedCandidateFromLinkedIn}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4 py-2">
                        <FormProvider {...form}>
                            <form onSubmit={handleSearch} className="flex flex-col gap-2">
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-2">
                                    <div className="min-w-0 flex-1">
                                        <Label htmlFor="linkedInId" className="mb-1 flex items-center gap-1">
                                            <LinkedInIcon className="text-primary size-4" />
                                            {UIStrings.SearchByLinkedInUrl}
                                        </Label>
                                        <Input
                                            id="linkedInId"
                                            {...form.register('linkedInId')}
                                            placeholder={UIStrings.SearchByLinkedInUrlPlaceholder}
                                            autoComplete="off"
                                            aria-invalid={!!form.formState.errors.linkedInId?.message}
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={isSearching}
                                        className="shrink-0 self-stretch sm:self-end"
                                    >
                                        {isSearching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        {UIStrings.Search}
                                    </Button>
                                </div>
                                {form.formState.errors.linkedInId?.message && (
                                    <FieldError>{form.formState.errors.linkedInId.message}</FieldError>
                                )}
                            </form>
                        </FormProvider>
                        {isSearching && (
                            <p className="text-muted-foreground flex items-center gap-2 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {UIStrings.Searching}
                            </p>
                        )}
                        {searchResult && !searchError && (
                            <MatchingProfilesList profiles={[searchResult]} onSelectedProfiles={setSelectedProfiles} />
                        )}
                        {searchErrorMessage}
                    </div>
                    <DialogFooter className="gap-2 sm:justify-end">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isShortlisting}>
                            {UIStrings.Cancel}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleShortlist}
                            disabled={!selectedProfiles.length || isShortlisting}
                        >
                            {isShortlisting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {UIStrings.AddToShortlist}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AddFromLinkedInButton;
